import React, { useEffect, useMemo, useState } from 'react';
import { merchantService } from '../services/merchantService';
import { documentService } from '../services/documentService';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { FileText, Edit2, Save } from 'lucide-react';

const Company = () => {
  const [filters, setFilters] = useState({
    companyName: '',
    companyId: '',
    companyEmail: '',
    employeeName: '',
    employeeId: '',
    startDate: '',
    endDate: ''
  });
  const [rows, setRows] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [documentsByCompany, setDocumentsByCompany] = useState({});
  const [documentTypeId, setDocumentTypeId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    merchantService
      .getAll()
      .then((merchants) => {
        const companyRows = merchants.map((merchant) => ({
          companyId: merchant.orgId,
          companyName: merchant.orgName || '',
          companyEmail: merchant.orgEmail || '',
          employeeId: merchant.managerUserId || '',
          employeeName: '',
          status: 'active',
          merchant,
          companyInfo: {
            gst: '',
            pan: '',
            address: [merchant.address1, merchant.address2, merchant.city, merchant.state, merchant.country, merchant.zip]
              .filter(Boolean)
              .join(', '),
          },
          bankInfo: { accountName: '', accountNumber: '', ifsc: '', bankName: '' },
          managerInfo: { name: '', email: '', mobile: '' },
        }));
        setRows(companyRows);
        setSelectedCompanyId(companyRows[0]?.companyId || null);
      })
      .catch((apiError) => setError(apiError.userMessage || 'Unable to load companies.'));
  }, []);

  const filteredRows = useMemo(
    () =>
      rows.filter((row) => {
        const matchesCompanyName = String(row.companyName || '').toLowerCase().includes(filters.companyName.toLowerCase());
        const matchesCompanyId = String(row.companyId || '').toLowerCase().includes(filters.companyId.toLowerCase());
        const matchesCompanyEmail = String(row.companyEmail || '').toLowerCase().includes(filters.companyEmail.toLowerCase());
        const matchesEmployeeName = String(row.employeeName || '').toLowerCase().includes(filters.employeeName.toLowerCase());
        const matchesEmployeeId = String(row.employeeId || '').toLowerCase().includes(filters.employeeId.toLowerCase());
        const matchesStartDate = !filters.startDate || row.createdAt >= filters.startDate;
        const matchesEndDate = !filters.endDate || row.createdAt <= filters.endDate;
        return (
          matchesCompanyName &&
          matchesCompanyId &&
          matchesCompanyEmail &&
          matchesEmployeeName &&
          matchesEmployeeId &&
          matchesStartDate &&
          matchesEndDate
        );
      }),
    [filters, rows]
  );

  const selectedCompany = useMemo(
    () => rows.find((row) => row.companyId === selectedCompanyId) || rows[0],
    [rows, selectedCompanyId]
  );

  const currentDocuments = documentsByCompany[selectedCompany?.companyId] || [];

  const handleFieldUpdate = (section, field, value) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.companyId === selectedCompany.companyId
          ? {
              ...row,
              [section]: {
                ...row[section],
                [field]: value
              }
            }
          : row
      )
    );
  };

  const handleDocumentUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedCompany || !documentTypeId) {
      setError('Select a document type ID before uploading.');
      return;
    }
    try {
      await documentService.upload({
        file,
        orgId: selectedCompany.companyId,
        documentTypeId,
      });
      const now = new Date().toISOString().slice(0, 10);
      setDocumentsByCompany((prev) => ({
        ...prev,
        [selectedCompany.companyId]: [
          ...(prev[selectedCompany.companyId] || []),
          { id: `${selectedCompany.companyId}-${file.name}`, name: file.name, uploadedOn: now },
        ],
      }));
      setError('');
    } catch (apiError) {
      setError(apiError.userMessage || 'Unable to upload document.');
    }
    event.target.value = '';
  };

  const saveCompany = async () => {
    try {
      const updated = await merchantService.update(selectedCompany.companyId, {
        ...selectedCompany.merchant,
        orgName: selectedCompany.companyName,
        orgEmail: selectedCompany.companyEmail,
      });
      setRows((prev) =>
        prev.map((row) => (row.companyId === selectedCompany.companyId ? { ...row, merchant: updated } : row))
      );
      setIsEditing(false);
      setError('');
    } catch (apiError) {
      setError(apiError.userMessage || 'Unable to update company.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Company</h1>
        <p className="text-gray-500 mt-1">Search companies and manage details</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input
            placeholder="Company name"
            value={filters.companyName}
            onChange={(event) => setFilters((prev) => ({ ...prev, companyName: event.target.value }))}
          />
          <Input
            placeholder="Company id"
            value={filters.companyId}
            onChange={(event) => setFilters((prev) => ({ ...prev, companyId: event.target.value }))}
          />
          <Input
            placeholder="Company email"
            value={filters.companyEmail}
            onChange={(event) => setFilters((prev) => ({ ...prev, companyEmail: event.target.value }))}
          />
          <Input
            placeholder="Emp name"
            value={filters.employeeName}
            onChange={(event) => setFilters((prev) => ({ ...prev, employeeName: event.target.value }))}
          />
          <Input
            placeholder="Emp id"
            value={filters.employeeId}
            onChange={(event) => setFilters((prev) => ({ ...prev, employeeId: event.target.value }))}
          />
          <Input
            type="date"
            value={filters.startDate}
            onChange={(event) => setFilters((prev) => ({ ...prev, startDate: event.target.value }))}
          />
          <Input
            type="date"
            value={filters.endDate}
            onChange={(event) => setFilters((prev) => ({ ...prev, endDate: event.target.value }))}
          />
          <Button
            variant="outline"
            onClick={() =>
              setFilters({
                companyName: '',
                companyId: '',
                companyEmail: '',
                employeeName: '',
                employeeId: '',
                startDate: '',
                endDate: ''
              })
            }
          >
            Reset Filters
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Result Grid</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company Name</TableHead>
                  <TableHead>Company ID</TableHead>
                  <TableHead>Company Email</TableHead>
                  <TableHead>Emp Name</TableHead>
                  <TableHead>Emp ID</TableHead>
                  <TableHead>Created On</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.map((row) => (
                  <TableRow
                    key={row.companyId}
                    className={`cursor-pointer ${selectedCompanyId === row.companyId ? 'bg-blue-50' : ''}`}
                    onClick={() => {
                      setSelectedCompanyId(row.companyId);
                      setIsEditing(false);
                    }}
                  >
                    <TableCell>{row.companyName}</TableCell>
                    <TableCell>{row.companyId}</TableCell>
                    <TableCell>{row.companyEmail}</TableCell>
                    <TableCell>{row.employeeName}</TableCell>
                    <TableCell>{row.employeeId}</TableCell>
                    <TableCell>{row.createdAt}</TableCell>
                    <TableCell>
                      <Badge variant={row.status === 'active' ? 'default' : 'secondary'}>{row.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {selectedCompany && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle>Company Detail</CardTitle>
                <p className="text-sm text-gray-500 mt-1">Read-only by default. Use Edit to change data.</p>
              </div>
              <div className="flex items-center gap-2">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline">
                      <FileText className="h-4 w-4 mr-2" />
                      Documents
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-xl">
                    <DialogHeader>
                      <DialogTitle>Documents - {selectedCompany.companyName}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="companyDocUpload">Upload document</Label>
                        <Input
                          type="number"
                          placeholder="Document type ID"
                          value={documentTypeId}
                          onChange={(event) => setDocumentTypeId(event.target.value)}
                          className="mt-2"
                        />
                        <Input id="companyDocUpload" type="file" onChange={handleDocumentUpload} className="mt-2" />
                      </div>
                      <div className="border rounded-lg">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Document</TableHead>
                              <TableHead>Uploaded On</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {currentDocuments.map((doc) => (
                              <TableRow key={doc.id}>
                                <TableCell>{doc.name}</TableCell>
                                <TableCell>{doc.uploadedOn}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                <Button
                  variant={isEditing ? 'default' : 'outline'}
                  onClick={() => (isEditing ? saveCompany() : setIsEditing(true))}
                >
                  {isEditing ? <Save className="h-4 w-4 mr-2" /> : <Edit2 className="h-4 w-4 mr-2" />}
                  {isEditing ? 'Save' : 'Edit'}
                </Button>

              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <section className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900">Company Info</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Company Name</Label>
                  <Input
                    value={selectedCompany.companyName}
                    disabled={!isEditing}
                    onChange={(event) => {
                      const value = event.target.value;
                      setRows((prevRows) =>
                        prevRows.map((row) =>
                          row.companyId === selectedCompany.companyId ? { ...row, companyName: value } : row
                        )
                      );
                    }}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Company Email</Label>
                  <Input
                    value={selectedCompany.companyEmail}
                    disabled={!isEditing}
                    onChange={(event) => {
                      const value = event.target.value;
                      setRows((prevRows) =>
                        prevRows.map((row) =>
                          row.companyId === selectedCompany.companyId ? { ...row, companyEmail: value } : row
                        )
                      );
                    }}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>PAN</Label>
                  <Input
                    value={selectedCompany.companyInfo.pan}
                    disabled={!isEditing}
                    onChange={(event) => handleFieldUpdate('companyInfo', 'pan', event.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>GST</Label>
                  <Input
                    value={selectedCompany.companyInfo.gst}
                    disabled={!isEditing}
                    onChange={(event) => handleFieldUpdate('companyInfo', 'gst', event.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Address</Label>
                  <Input
                    value={selectedCompany.companyInfo.address}
                    disabled={!isEditing}
                    onChange={(event) => handleFieldUpdate('companyInfo', 'address', event.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900">Bank Info</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Account Name</Label>
                  <Input
                    value={selectedCompany.bankInfo.accountName}
                    disabled={!isEditing}
                    onChange={(event) => handleFieldUpdate('bankInfo', 'accountName', event.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Account Number</Label>
                  <Input
                    value={selectedCompany.bankInfo.accountNumber}
                    disabled={!isEditing}
                    onChange={(event) => handleFieldUpdate('bankInfo', 'accountNumber', event.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>IFSC</Label>
                  <Input
                    value={selectedCompany.bankInfo.ifsc}
                    disabled={!isEditing}
                    onChange={(event) => handleFieldUpdate('bankInfo', 'ifsc', event.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Bank Name</Label>
                  <Input
                    value={selectedCompany.bankInfo.bankName}
                    disabled={!isEditing}
                    onChange={(event) => handleFieldUpdate('bankInfo', 'bankName', event.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900">Manager Info</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Manager Name</Label>
                  <Input
                    value={selectedCompany.managerInfo.name}
                    disabled={!isEditing}
                    onChange={(event) => handleFieldUpdate('managerInfo', 'name', event.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Manager Email</Label>
                  <Input
                    value={selectedCompany.managerInfo.email}
                    disabled={!isEditing}
                    onChange={(event) => handleFieldUpdate('managerInfo', 'email', event.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Manager Mobile</Label>
                  <Input
                    value={selectedCompany.managerInfo.mobile}
                    disabled={!isEditing}
                    onChange={(event) => handleFieldUpdate('managerInfo', 'mobile', event.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </section>
          </CardContent>
        </Card>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default Company;
